import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { SubjectDTO } from '@/features/academic-structure/application/dto/SubjectDTO';

/**
 * Subjects Store State Interface
 * Manages global state for academic subjects
 */
interface SubjectsState {
  // Data
  subjects: SubjectDTO[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions - Setters
  setSubjects: (subjects: SubjectDTO[]) => void;
  addSubject: (subject: SubjectDTO) => void;
  updateSubject: (id: number, subject: Partial<SubjectDTO>) => void;
  deleteSubject: (id: number) => void;

  // Actions - State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Helpers
  getSubjectById: (id: number) => SubjectDTO | undefined;
  getSubjectsByLevel: (levelId: number) => SubjectDTO[];
  clearSubjects: () => void;
  reset: () => void;
}

/**
 * Initial state for subjects store
 */
const initialState = {
  subjects: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Subjects Store
 *
 * Global state management for subjects using Zustand.
 * Features:
 * - Persistent storage (localStorage)
 * - CRUD operations
 * - Loading and error states
 * - Helper selectors
 *
 * Usage:
 * ```typescript
 * const { subjects, isLoading, setSubjects } = useSubjectsStore();
 * ```
 *
 * Note: This store should be populated by use cases, not repositories directly.
 * Follow hexagonal architecture principles.
 */
export const useSubjectsStore = create<SubjectsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Setters
      setSubjects: (subjects) =>
        set({
          subjects,
          lastFetched: Date.now(),
          error: null,
        }),

      addSubject: (subject) =>
        set((state) => ({
          subjects: [...state.subjects, subject],
          lastFetched: Date.now(),
        })),

      updateSubject: (id, updatedSubject) =>
        set((state) => ({
          subjects: state.subjects.map((subject) =>
            subject.id === id ? { ...subject, ...updatedSubject } : subject
          ),
          lastFetched: Date.now(),
        })),

      deleteSubject: (id) =>
        set((state) => ({
          subjects: state.subjects.filter((subject) => subject.id !== id),
          lastFetched: Date.now(),
        })),

      // State Management
      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error, isLoading: false }),

      // Helpers
      getSubjectById: (id) => {
        const state = get();
        return state.subjects.find((subject) => subject.id === id);
      },

      getSubjectsByLevel: (levelId) => {
        const state = get();
        return state.subjects.filter((subject) => subject.levelId === levelId);
      },

      clearSubjects: () =>
        set({
          subjects: [],
          lastFetched: null,
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'subjects-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not loading/error states
      partialize: (state) => ({
        subjects: state.subjects,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

/**
 * Selectors for optimized component rendering
 * Use these to select specific parts of the state
 */
export const subjectsSelectors = {
  // Select all subjects
  selectSubjects: (state: SubjectsState) => state.subjects,

  // Select loading state
  selectIsLoading: (state: SubjectsState) => state.isLoading,

  // Select error state
  selectError: (state: SubjectsState) => state.error,

  // Select subject by ID (returns a selector function)
  selectSubjectById: (id: number) => (state: SubjectsState) =>
    state.subjects.find((subject) => subject.id === id),

  // Select subjects by level
  selectSubjectsByLevel: (levelId: number) => (state: SubjectsState) =>
    state.subjects.filter((subject) => subject.levelId === levelId),

  // Select subjects count
  selectSubjectsCount: (state: SubjectsState) => state.subjects.length,

  // Check if data is stale (older than 5 minutes)
  selectIsStale: (state: SubjectsState) => {
    if (!state.lastFetched) return true;
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() - state.lastFetched > fiveMinutes;
  },
};
